import type { CampLocation } from "@/types/campLocation";
import type { Offer } from "@/types/offer";
import {
  collectPlaceholderDiscoveryExceptionKeys,
  type PlaceholderDiscoveryVariation
} from "./placeholderDiscoveryException";

/** Parse Firestore Timestamp or Date from offer fields. */
export function offerTimestampToDate(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const t = value as { toDate?: () => Date };
  if (typeof t.toDate === "function") {
    const d = t.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  }
  return null;
}

function startOfUtcDayFromDate(now: Date): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
}

/** Subtract calendar months in the UTC calendar (clamps day to month length). */
function subtractUtcMonths(baseStartOfUtcDay: Date, months: number): Date {
  const y = baseStartOfUtcDay.getUTCFullYear();
  const m = baseStartOfUtcDay.getUTCMonth();
  const d = baseStartOfUtcDay.getUTCDate();
  const totalMonths = y * 12 + m - months;
  const ny = Math.floor(totalMonths / 12);
  const nm = totalMonths % 12;
  const dim = new Date(Date.UTC(ny, nm + 1, 0)).getUTCDate();
  const dd = Math.min(d, dim);
  return new Date(Date.UTC(ny, nm, dd, 0, 0, 0, 0));
}

/**
 * Non-placeholder offers that drive which program keys get placeholder sessions:
 * - endDate is on or after the start of the current UTC day (upcoming / in progress), or
 * - endDate is before start of today UTC but on or after (today − 13 calendar months) UTC.
 */
export function isOfferRelevantForPlaceholderDiscovery(
  offer: Pick<Offer, "isPlaceholder" | "variationKey" | "endDate">,
  now: Date = new Date()
): boolean {
  if (offer.isPlaceholder === true) return false;
  const vk = typeof offer.variationKey === "string" ? offer.variationKey.trim() : "";
  if (!vk) return false;
  const end = offerTimestampToDate(offer.endDate);
  if (!end) return false;

  const startOfTodayUtc = startOfUtcDayFromDate(now);
  const thirteenMonthsAgoUtc = subtractUtcMonths(startOfTodayUtc, 13);

  if (end >= startOfTodayUtc) return true;
  return end >= thirteenMonthsAgoUtc && end < startOfTodayUtc;
}

export function collectVariationKeysForPlaceholdersFromOffers(
  offers: readonly Pick<Offer, "isPlaceholder" | "variationKey" | "endDate">[],
  now?: Date
): Set<string> {
  const t = now ?? new Date();
  const keys = new Set<string>();
  for (const o of offers) {
    if (!isOfferRelevantForPlaceholderDiscovery(o, t)) continue;
    const vk = typeof o.variationKey === "string" ? o.variationKey.trim() : "";
    if (vk) keys.add(vk);
  }
  return keys;
}

/**
 * The full placeholder discovery set: keys from the 13-month rule over real offers, plus keys of
 * variations whose `placeholderDiscoveryUntil` exception is active today (UTC).
 */
export function collectVariationKeysForPlaceholders(
  offers: readonly Pick<Offer, "isPlaceholder" | "variationKey" | "endDate">[],
  variations: readonly PlaceholderDiscoveryVariation[] | null | undefined,
  now?: Date
): Set<string> {
  const t = now ?? new Date();
  const keys = collectVariationKeysForPlaceholdersFromOffers(offers, t);
  for (const k of collectPlaceholderDiscoveryExceptionKeys(variations, t).activeKeys) keys.add(k);
  return keys;
}

/**
 * Expected placeholder offer count for a published location when real offers are known.
 * Pass all offers for the location (placeholders + non-placeholders); only non-placeholder
 * sessions in the discovery window, and active `placeholderDiscoveryUntil` exceptions on the
 * location's variations, affect the result.
 */
export function computeExpectedPlaceholderOffersCount(
  location: CampLocation | any,
  offersForLocation: readonly Pick<Offer, "isPlaceholder" | "variationKey" | "endDate">[] | null | undefined,
  now?: Date
): number {
  if (!location) return 0;
  if (location.visibility !== "v_published") return 0;
  const list = offersForLocation ?? [];
  return collectVariationKeysForPlaceholders(list, location.variations, now).size;
}
